import { ivFromPreset, IV_LIB, ivSuggest, withItems } from "./intervals";
import type { Preset } from "./intervals";
import { GROUPS } from "./defaults";
import { num } from "./normalize";
import type { CarSummary, GarageSummary, ScheduleLine, Status } from "./schedule";
import type { Car, Entry, GarageState, Group } from "./types";

/** Pure workings behind the screens, each named after the current app's function it matches. */

export const STATUS_LABEL: Readonly<Record<Status, string>> = { overdue: "Overdue", soon: "Due soon", ok: "OK", none: "No record" };

/** A new record id, same form as the current app (`uid`). */
export const newId = (prefix: string): string => `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

export const carLabel = (c: Pick<Car, "year" | "make" | "model">): string => [c.year, c.make, c.model].filter(Boolean).join(" ");
export const hasOwnSchedule = (c: Car | null | undefined): boolean => !!c?.intervals && Object.keys(c.intervals).length > 0;
export const scheduleName = (c: Car): string =>
  hasOwnSchedule(c) ? IV_LIB.find((p) => p.id === c.intervalsFrom)?.name ?? "Own schedule" : "Default schedule";

/** "1,200 km over or 3 days late" (`fmtLeft`). `n` formats whole numbers. */
export function timeLeft(s: ScheduleLine, unit: string, n: (v: number) => string): string {
  const parts: string[] = [];
  const kl = s.kl ?? null, dl = s.dl ?? null;
  if (s.st === "overdue") {
    if (kl != null && kl < 0) parts.push(`${n(-kl)} ${unit} over`);
    if (dl != null && dl < 0) parts.push(`${n(-dl)}${dl === -1 ? " day" : " days"} late`);
  } else {
    if (kl != null) parts.push(`${n(kl)} ${unit} left`);
    if (dl != null) parts.push(dl === 0 ? "due today" : `in ${n(dl)}${dl === 1 ? " day" : " days"}`);
  }
  return parts.join(" or ");
}

export interface AttentionItem extends ScheduleLine { cd: CarSummary }

/** Everything overdue or due soon, most urgent first (Overview "Needs attention"). */
export function needsAttention(summary: GarageSummary, warnDist: number): AttentionItem[] {
  const urg = (s: ScheduleLine) => Math.min(s.kl != null ? (s.kl / Math.max(warnDist, 1)) * 30 : 1e9, s.dl ?? 1e9);
  return [...summary.cars.values()]
    .flatMap((cd) => cd.sched.filter((s) => s.st === "overdue" || s.st === "soon").map((s) => ({ ...s, cd })))
    .sort((a, b) => (a.st === b.st ? 0 : a.st === "overdue" ? -1 : 1) || urg(a) - urg(b));
}

/** The attention list's order inside one car or across cars on the Schedule page. */
export const STATUS_ORDER: Readonly<Record<Status, number>> = { overdue: 0, soon: 1, none: 2, ok: 3 };

export interface MonthColumn { start: Date; ym: string; parts: Partial<Record<Group, number>>; total: number }

const ymOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/** The last 12 months of spending by type (`monthCols`). */
export function monthColumns(entries: readonly Entry[], summary: GarageSummary): MonthColumn[] {
  const t = summary.today, cols: MonthColumn[] = [];
  for (let k = 11; k >= 0; k--) {
    const start = new Date(t.getFullYear(), t.getMonth() - k, 1), end = new Date(t.getFullYear(), t.getMonth() - k + 1, 1);
    const parts: MonthColumn["parts"] = {};
    let total = 0;
    for (const e of entries) {
      const d = entryDate(e);
      if (d && d >= start && d < end) {
        const g = groupOf(e, summary), a = num(e.amount) || 0;
        parts[g] = (parts[g] ?? 0) + a;
        total += a;
      }
    }
    cols.push({ start, ym: ymOf(start), parts, total });
  }
  return cols;
}

export const usedGroups = (cols: readonly MonthColumn[]): Group[] => GROUPS.filter((g) => cols.some((c) => (c.parts[g] ?? 0) > 0));

export const groupOf = (e: Entry, summary: GarageSummary): Group => summary.groups.get(e.item) ?? "Other";

/** Local-midnight date of an entry, or null. */
export function entryDate(e: Entry): Date | null {
  if (typeof e.date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(e.date)) return null;
  const [y, m, d] = e.date.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

/** Round axis steps for a chart (`niceScale`). */
export function niceScale(max: number, ticks = 4): { max: number; ticks: number[] } {
  if (!(max > 0)) return { max: 1, ticks: [0, 1] };
  const raw = max / ticks, p = Math.pow(10, Math.floor(Math.log10(raw))), m = raw / p;
  const step = (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p;
  const top = Math.ceil(max / step - 1e-9) * step, out: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return { max: top, ticks: out };
}

export interface LogFilter { q: string; car: string; group: string; year: string; month: string }

export const EMPTY_LOG_FILTER: LogFilter = { q: "", car: "", group: "", year: "", month: "" };

/** Reads the log's filters from the address (`applyQuery`); anything malformed is ignored. */
export function parseLogFilter(q: URLSearchParams): LogFilter {
  const g = q.get("group") ?? "", y = q.get("year") ?? "", m = q.get("month") ?? "";
  return {
    q: q.get("q") ?? "",
    car: q.get("car") ?? "",
    group: (GROUPS as readonly string[]).includes(g) ? g : "",
    year: /^\d{4}$/.test(y) ? y : "",
    month: /^\d{4}-\d{2}$/.test(m) ? m : "",
  };
}

/** The address for the log's filters (`syncLogHash`). */
export function logFilterParams(f: LogFilter): URLSearchParams {
  const p = new URLSearchParams();
  for (const k of ["car", "group", "year", "month"] as const) if (f[k]) p.set(k, f[k]);
  if (f.q.trim()) p.set("q", f.q.trim());
  return p;
}

/** Newest first; search looks in item, description, shop, notes, plate and "diy myself" (`filterLog`). */
export function filterLog(st: GarageState, summary: GarageSummary, f: LogFilter): Entry[] {
  const q = f.q.trim().toLowerCase();
  const plate = (id: string) => st.cars.find((c) => c.id === id)?.plate ?? "Unknown car";
  const rows = st.log.filter((e) => {
    const d = entryDate(e);
    return (!f.car || e.car === f.car)
      && (!f.group || groupOf(e, summary) === f.group)
      && (!f.year || (d != null && d.getFullYear() === +f.year))
      && (!f.month || String(e.date ?? "").startsWith(f.month))
      && (!q || [e.item, e.desc, e.shop, e.notes, plate(e.car), e.diy ? "diy myself" : ""].join(" ").toLowerCase().includes(q));
  });
  return sortEntries(rows);
}

/** Newest first, then highest reading. */
export function sortEntries(rows: readonly Entry[]): Entry[] {
  const t = (e: Entry) => entryDate(e)?.getTime() ?? 0;
  return [...rows].sort((a, b) => t(b) - t(a) || (num(b.odo) || 0) - (num(a.odo) || 0));
}

export interface CustomerGroup {
  k: string; name: string; contact: string; tel: string; cars: CarSummary[]; last: Date | null; spent: number; worst: Status;
}

/** Cars grouped by owner, most recent visit first, cars with no owner last (`customerGroups`). */
export function customerGroups(summary: GarageSummary): CustomerGroup[] {
  const g = new Map<string, CustomerGroup>();
  const rank: Record<Status, number> = { overdue: 3, soon: 2, ok: 1, none: 0 };
  for (const cd of summary.cars.values()) {
    const name = String(cd.car.owner ?? "").trim(), tel = String(cd.car.contact ?? "").replace(/[^\d+]/g, "");
    const k = name || tel ? `${name.toLowerCase()}|${tel}` : "~none";
    let x = g.get(k);
    if (!x) {
      x = { k, name: name || (tel ? "No name" : ""), contact: String(cd.car.contact ?? ""), tel, cars: [], last: null, spent: 0, worst: "ok" };
      g.set(k, x);
    }
    x.cars.push(cd);
    for (const e of cd.entries) {
      const d = entryDate(e);
      if (d && (!x.last || d > x.last)) x.last = d;
      x.spent += num(e.amount) || 0;
    }
    if (rank[cd.st] > rank[x.worst]) x.worst = cd.st;
  }
  return [...g.values()].sort((a, b) => (a.k === "~none" ? 1 : b.k === "~none" ? -1 : (b.last?.getTime() ?? 0) - (a.last?.getTime() ?? 0)));
}

/** Search across a customer's name, number and cars (`custCards`). */
export function matchesCustomer(x: CustomerGroup, q: string): boolean {
  const lc = q.toLowerCase();
  return !lc || [x.name, x.contact, ...x.cars.map((cd) => `${cd.car.plate} ${cd.car.make ?? ""} ${cd.car.model ?? ""}`)].join(" ").toLowerCase().includes(lc);
}

export const carSearchText = (c: Car): string =>
  [c.plate, c.nick, c.make, c.model, c.year, c.owner, c.contact].filter(Boolean).join(" ").toLowerCase();

/** Cars that could use their maker's schedule and haven't said no (`ivOffers`). */
export function makerOffers(st: GarageState): { car: Car; preset: Preset }[] {
  return st.cars.flatMap((car) => {
    if (car.sample || hasOwnSchedule(car) || car.ivNo) return [];
    const preset = ivSuggest(car);
    return preset ? [{ car, preset }] : [];
  });
}

/** Puts a maker's schedule on one car (`presetTo` + `ivApply`). */
export function applyPreset(st: GarageState, carId: string, p: Preset, at = Date.now()): GarageState {
  const iv = ivFromPreset(p, st.settings.dist);
  const names = Object.keys(iv).filter((n) => { const o = iv[n]; return o && !("off" in o); });
  const cars = st.cars.map((c) => {
    if (c.id !== carId) return c;
    const next: Car = { ...c, intervals: iv, intervalsFrom: p.id, u: at };
    delete next.ivNo;
    return next;
  });
  return { ...st, cars, settings: { ...st.settings, items: withItems(st.settings.items, names) }, meta: { ...st.meta, itemsReviewed: true } };
}

/** Removes a car and its entries (`deleteCar`). */
export function removeCar(st: GarageState, id: string): GarageState {
  return { ...st, cars: st.cars.filter((c) => c.id !== id), log: st.log.filter((e) => e.car !== id) };
}
